import type { SuperHubHashRouteParams } from '@routes/SuperHub/types';
import type { RefObject } from 'react';

import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useSuperHubStore } from '@core/store';
import { SuperHubHashRoutePaths } from '@routes/SuperHub/types';

type ScrollTargetRef = RefObject<HTMLElement | null>;

/**
 * Checks if the current page was navigated to with a text fragment (e.g. `#:~:text=foo`).
 * Browsers intentionally hide text fragments from `location.hash`, so we check the
 * performance API instead.
 * @see https://web.dev/text-fragments
 */
function hasTextFragment() {
  try {
    const performanceName = performance.getEntries().find(({ entryType }) => entryType === 'navigation')?.name;
    if (performanceName) {
      return new URL(performanceName).hash.includes(':~:');
    }
  } catch (e) {
    // noop
  }
  return false;
}

export function scrollToTop(isSmoothScroll = false, target?: ScrollTargetRef) {
  const el = target?.current;

  if (el && typeof el.scrollTo === 'function') {
    el.scrollTo({
      top: 0,
      left: 0,
      behavior: isSmoothScroll ? 'smooth' : 'auto',
    });
  } else {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: isSmoothScroll ? 'smooth' : 'auto',
    });
  }
}
export default function ScrollTop({
  smooth = false,
  deps,
  target,
}: {
  deps?: string[] | undefined;
  smooth?: boolean;
  target?: ScrollTargetRef;
}) {
  const [isSuperHub, isDocumentLoading] = useSuperHubStore(s => [s.isSuperHub, s.document.isLoading]);

  const { hash, pathname } = useLocation();

  const dependencies = Array.isArray(deps)
    ? [...deps, target, isDocumentLoading]
    : [pathname, hash, target, isDocumentLoading];

  useEffect(() => {
    if (isSuperHub && isDocumentLoading) return;

    const scrollTop = target?.current ? target?.current.scrollTop : window.scrollY;
    // check if hash is valid for jumping to a heading
    const isRouterHash = !!matchPath<SuperHubHashRouteParams>(hash.substring(1), Object.values(SuperHubHashRoutePaths));
    const hasTargetHash = hash.includes('#') && hash !== '#/' && hash !== '#' && !isRouterHash;

    if (isSuperHub) {
      // The SuperHub hash router is always mounted in admin mode, and it will pollute an anchor target hash with the root `/`
      // eg. `#some-heading` becomes `#/some-heading`. So we need to account for that.
      if (hasTargetHash) {
        // If the current hash does not match a valid SuperHub hash route, treat it as an anchor target hash
        const cleanHash = hash.startsWith('#/') ? hash.slice(2) : hash.startsWith('#') ? hash.slice(1) : hash;
        // We can't supply `cleanHash` directly to `querySelector` because it may contain, or begin
        // with, characters that are not valid selectors (eg. `#3-why-do-i-see-a-data-discrepancy`).

        // Defer the selection of the element to the next animation frame to ensure the target is rendered.
        // Without it, we sometimes fail to find the target element in the DOM.
        window.requestAnimationFrame(() => {
          try {
            const heading = document.querySelector(`[id="${decodeURIComponent(cleanHash)}"]`);
            if (heading) heading.scrollIntoView();
          } catch {
            // If we can't run the query selector then we should just not scroll to the heading.
            // This will happen if the ID in question is malformed or an XSS attack.
            // https://readme.sentry.io/issues/7141374786
          }
        });
        return; // Don't scroll to top when we're scrolling to a heading
      }

      // Don't scroll to top if the URL contains a text fragment
      if (hasTextFragment()) return;

      scrollToTop(smooth, target);
      return;
    }

    // Don't scroll if the URL contains a hash parameter, a text fragment,
    // or if the window scroll position is already the top
    if (window.location.hash || hasTextFragment() || scrollTop === 0) return;
    scrollToTop(smooth, target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return null;
}
